import { z } from "zod";
import type { PrimaryRole } from "../../auth-account-contracts";

const dashboardServiceKeys = [
  "stopwatch",
  "planner",
  "marketplace",
  "stable",
  "hire",
  "account",
] as const;

export type DashboardServiceKey = (typeof dashboardServiceKeys)[number];

interface DashboardServiceUsage {
  readonly count: number;
  readonly lastUsedAt: number;
}

export type DashboardUsage = Partial<
  Readonly<Record<DashboardServiceKey, DashboardServiceUsage>>
>;

const QUICK_ACCESS_LIMIT = 4;

const generalQuickAccess = [
  "stopwatch",
  "planner",
  "marketplace",
  "stable",
] as const satisfies readonly DashboardServiceKey[];

export function defaultQuickAccess(
  primaryRole: PrimaryRole | null,
): readonly DashboardServiceKey[] {
  switch (roleGroup(primaryRole)) {
    case "owner":
      return ["planner", "marketplace", "stable", "hire"];
    case "trainer":
      return ["stable", "stopwatch", "planner", "hire"];
    case "staff":
      return ["stopwatch", "stable", "hire", "account"];
    case "care":
      return ["stable", "stopwatch", "planner", "account"];
    case "vendor":
      return ["marketplace", "planner", "hire", "account"];
    case "general":
      return generalQuickAccess;
  }
}

export function rankedQuickAccess(
  primaryRole: PrimaryRole | null,
  usage: DashboardUsage,
): readonly DashboardServiceKey[] {
  const defaults = defaultQuickAccess(primaryRole);
  const fallbackOrder = [
    ...defaults,
    ...dashboardServiceKeys.filter((key) => !defaults.includes(key)),
  ];

  return fallbackOrder
    .toSorted((left, right) => {
      const leftUsage = usage[left];
      const rightUsage = usage[right];
      const countDifference =
        (rightUsage?.count ?? 0) - (leftUsage?.count ?? 0);
      if (countDifference !== 0) return countDifference;

      const recencyDifference =
        (rightUsage?.lastUsedAt ?? 0) - (leftUsage?.lastUsedAt ?? 0);
      if (recencyDifference !== 0) return recencyDifference;

      return fallbackOrder.indexOf(left) - fallbackOrder.indexOf(right);
    })
    .slice(0, QUICK_ACCESS_LIMIT);
}

type DashboardRoleGroup =
  "owner" | "trainer" | "staff" | "care" | "vendor" | "general";

function roleGroup(primaryRole: PrimaryRole | null): DashboardRoleGroup {
  switch (primaryRole) {
    case "OWNER":
      return "owner";
    case "TRAINER":
    case "ASSISTANT_TRAINER":
    case "RACING_MANAGER":
      return "trainer";
    case "RIDER":
    case "GROOM":
    case "HEAD_LAD":
    case "FEEDMAN":
    case "TRAVELLING_LAD":
    case "TRAVELLING_HEAD_LAD":
      return "staff";
    case "VET":
    case "FARRIER":
      return "care";
    case "VENDOR":
      return "vendor";
    case null:
      return "general";
  }
}

export function dashboardServiceFromPathname(
  pathname: string,
  locale: string,
): DashboardServiceKey | null {
  const normalizedPath = pathname.replace(/\/+$/u, "") || "/";
  const strippedPath = normalizedPath.startsWith(`/${locale}`)
    ? normalizedPath.slice(locale.length + 1) || "/"
    : normalizedPath;

  return (
    dashboardServiceKeys.find(
      (key) =>
        strippedPath === `/${key}` || strippedPath.startsWith(`/${key}/`),
    ) ?? null
  );
}

const ServiceUsageSchema = z
  .object({
    count: z.number().int().min(1).max(Number.MAX_SAFE_INTEGER),
    lastUsedAt: z.number().int().nonnegative(),
  })
  .strict();

const StoredDashboardUsageSchema = z
  .object({
    version: z.literal(1),
    services: z.record(z.enum(dashboardServiceKeys), ServiceUsageSchema),
  })
  .strict();

interface DashboardUsageStorage {
  readonly getItem: (key: string) => string | null;
  readonly setItem: (key: string, value: string) => void;
  readonly removeItem: (key: string) => void;
}

export function readDashboardUsage(
  storage: DashboardUsageStorage,
  accountId: string,
): DashboardUsage {
  return parseDashboardUsageSnapshot(
    dashboardUsageSnapshot(storage, accountId),
  );
}

export function dashboardUsageSnapshot(
  storage: DashboardUsageStorage,
  accountId: string,
): string | null {
  try {
    return storage.getItem(storageKey(accountId));
  } catch {
    return null;
  }
}

export function parseDashboardUsageSnapshot(
  snapshot: string | null,
): DashboardUsage {
  if (snapshot === null) return {};
  try {
    return StoredDashboardUsageSchema.parse(JSON.parse(snapshot)).services;
  } catch {
    return {};
  }
}

export function recordDashboardServiceVisit(
  storage: DashboardUsageStorage,
  accountId: string,
  service: DashboardServiceKey,
  now: number,
): boolean {
  try {
    const usage = readDashboardUsage(storage, accountId);
    const current = usage[service];
    const count = Math.min((current?.count ?? 0) + 1, Number.MAX_SAFE_INTEGER);
    storage.setItem(
      storageKey(accountId),
      JSON.stringify({
        version: 1,
        services: {
          ...usage,
          [service]: { count, lastUsedAt: now },
        },
      }),
    );
    return true;
  } catch {
    return false;
  }
}

export function clearDashboardUsage(
  storage: DashboardUsageStorage,
  accountId: string,
): boolean {
  try {
    storage.removeItem(storageKey(accountId));
    return true;
  } catch {
    return false;
  }
}

function storageKey(accountId: string): string {
  return `hisani.dashboard-usage.v1.${accountId}`;
}
